# Position openings

## Before you begin

Position opening operations require a [partner token](/auth/partner-tokens).

Our [data model] provides further context on position openings and their relation to position profiles.

## Operations

The following sample mutations are provided for demonstration purposes only;
they are [not executable in the Playground environment](/graphql/playground).
You should use your live partner credentials to post a job ad under your [unsearchable SEEK test hirer](/introduction#integration-process) for end-to-end testing.

### createPositionOpening

The [`createPositionOpening` mutation] creates an empty position opening.
The [`postingRequester`] indicates the SEEK hirer and the [contact details] of the opening’s owner.

```graphql
mutation ($input: CreatePositionOpeningInput!) {
  createPositionOpening(input: $input) {
    positionOpening {
      documentId {
        value
      }
    }
  }
}
```

```json
{
  "input": {
    "positionOpening": {
      "postingRequester": {
        "roleCode": "Company",
        "id": "seekAnzPublicTest:organization:seek:93WyyF1h",
        "personContacts": [
          {
            "name": { "formattedName": "Jeff Manager" },
            "roleCode": "HiringManager",
            "communication": {
              "email": [{ "address": "jeff.manager@example.com" }],
              "phone": [{ "formattedNumber": "03 8517 4100" }]
            }
          }
        ]
      },
      "seekPartnerMetadata": "position-requisition-123"
    }
  }
}
```

```json
{
  "createPositionOpening": {
    "positionOpening": {
      "documentId": {
        "value": "globalPublicTest:positionOpening:positions:E3Lz6HkSrbZCawh4gPGAtM"
      }
    }
  }
}
```

### updatePositionOpeningStatus

The [`updatePositionOpeningStatus` mutation] updates the status of the position opening.

A position opening’s status is intended to help hirers manage their position openings;
it isn’t used directly by the SEEK API.
For example, you can filter on a particular status when [paginating position openings](#positionopenings).

```graphql
mutation ($input: UpdatePositionOpeningStatusInput!) {
  updatePositionOpeningStatus(input: $input) {
    positionOpening {
      statusCode
    }
  }
}
```

```json
{
  "input": {
    "positionOpening": {
      "documentId": "globalPublicTest:positionOpening:positions:E3Lz6HkSrbZCawh4gPGAtM",
      "statusCode": "Closed"
    }
  }
}
```

```json
{
  "updatePositionOpeningStatus": {
    "positionOpening": {
      "statusCode": "Closed"
    }
  }
}
```

After an opening has been created you can use separate mutations to create nested [`PositionProfile`s].
For example, the [`postPositionProfileForOpening` mutation] will create a job ad within the opening.

### updatePositionOpeningPersonContacts

The [`updatePositionOpeningPersonContacts` mutation] updates the `postingRequester` [contact details] of the position opening.

```graphql
mutation ($input: UpdatePositionOpeningPersonContactsInput!) {
  updatePositionOpeningPersonContacts(input: $input) {
    positionOpening {
      documentId {
        value
      }
    }
  }
}
```

```json
{
  "input": {
    "positionOpening": {
      "documentId": "globalPublicTest:positionOpening:positions:E3Lz6HkSrbZCawh4gPGAtM",
      "personContacts": [
        {
          "name": {
            "given": "Gabriella",
            "family": "Hettinger",
            "formattedName": "Gabriella Hettinger"
          },
          "communication": {
            "phone": [{ "formattedNumber": "09 2695 0948" }],
            "email": [{ "address": "g.hettinger@example.com" }]
          },
          "roleCode": "HiringManager"
        },
        {
          "name": {
            "given": "Matthew",
            "family": "Roberts",
            "formattedName": "Matthew Roberts"
          },
          "communication": {
            "phone": [{ "formattedNumber": "09 6523 3714" }],
            "email": [{ "address": "matty_r32@example.com" }]
          },
          "roleCode": "Recruiter"
        }
      ]
    }
  }
}
```

```json
{
  "updatePositionOpeningPersonContacts": {
    "positionOpening": {
      "documentId": {
        "value": "globalPublicTest:positionOpening:positions:E3Lz6HkSrbZCawh4gPGAtM"
      }
    }
  }
}
```

### positionOpening

The [`positionOpening` query] returns information about an existing position opening.
You can select fields from its nested [`PositionOpening.paginatedPositionProfiles`] to retrieve information about a subset of its job ads.

```graphql
query ($id: String!) {
  positionOpening(id: $id) {
    documentId {
      value
    }
    statusCode
    paginatedPositionProfiles {
      edges {
        node {
          profileId {
            value
          }
          positionTitle
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
    postingRequester {
      id {
        value
      }
    }
    seekPartnerMetadata
  }
}
```

```json
{
  "id": "globalPublicTest:positionOpening:positions:F65aT5rAv3ZSusuunFWT8i"
}
```

```json
{
  "positionOpening": {
    "documentId": {
      "value": "globalPublicTest:positionOpening:positions:F65aT5rAv3ZSusuunFWT8i"
    },
    "statusCode": "Active",
    "paginatedPositionProfiles": {
      "edges": [
        {
          "node": {
            "profileId": {
              "value": "seekAnzPublicTest:positionProfile:jobAd:27cuZeA47"
            },
            "positionTitle": "Customer Mobility Director"
          }
        }
      ],
      "pageInfo": {
        "hasNextPage": false,
        "endCursor": "globalPublicTest:positionProfileForOpeningCursor:positions:2gffoubCoNbaVMEAKupmSyz7WF6SwX35G5G6qdMRK3yxmHbsjppVMdB48W273ZELzjSaiaLtMHpjeEPyBKdzot38C3qY4wpdwDa"
      }
    },
    "postingRequester": {
      "id": {
        "value": "seekAnzPublicTest:organization:seek:93WyyF1h"
      }
    },
    "seekPartnerMetadata": "{\"id\":456,\"title\":\"Customer Mobility Director\"}"
  }
}
```

### positionOpenings

The [`positionOpenings` query] returns a paginated list of position openings for a given hirer.
Only position openings created by the SEEK API will appear in the paginated list.

You can optionally use a `statusCode` filter to only return position openings with the desired status.

```graphql
query ($hirerId: String!, $statusCode: String) {
  positionOpenings(hirerId: $hirerId, filter: { statusCode: $statusCode }) {
    edges {
      node {
        paginatedPositionProfiles {
          edges {
            node {
              profileId {
                value
              }
            }
          }
          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }
    }
    pageInfo {
      hasNextPage
      endCursor
    }
  }
}
```

```json
{
  "hirerId": "seekAnzPublicTest:organization:seek:93WyyF1h",
  "statusCode": "Active"
}
```

### deletePositionOpening

The [`deletePositionOpening` mutation] deletes an empty position opening.

Because every position profile must be nested inside an opening, you must delete all nested position profiles first:

- Unposted position profiles can be deleted using the [`deleteUnpostedPositionProfile` mutation].

- The position profiles of job ads cannot be explicitly deleted; they will be automatically deleted 180 days after the job ad closes.

To ensure data consistency, deletion of a position opening is prevented for 48 hours after it has been:

- Created through the [`postPosition` mutation]
- Referenced in a [`postPositionProfileForOpening` mutation]

To soft delete a position opening you can instead [update its status](#updatepositionopeningstatus) to `Closed`.
You can then filter on `Active` position openings when using the [`positionOpenings` query].

```graphql
mutation ($input: DeletePositionOpeningInput!) {
  deletePositionOpening(input: $input) {
    positionOpening {
      documentId {
        value
      }
    }
  }
}
```

```json
{
  "input": {
    "positionOpening": {
      "documentId": "globalPublicTest:positionOpening:positions:E3Lz6HkSrbZCawh4gPGAtM"
    }
  }
}
```

```json
{
  "deletePositionOpening": {
    "positionOpening": {
      "documentId": {
        "value": "globalPublicTest:positionOpening:positions:E3Lz6HkSrbZCawh4gPGAtM"
      }
    }
  }
}
```

[`createPositionOpening` mutation]: /schema/mutation/createPositionOpening
[`deletePositionOpening` mutation]: /schema/mutation/deletePositionOpening
[`deleteUnpostedPositionProfile` mutation]: /schema/mutation/deleteUnpostedPositionProfile
[`PositionOpening.paginatedPositionProfiles`]: /schema/named-type/PositionOpening#/field/paginatedPositionProfiles
[`positionOpening` query]: /schema/query/positionOpening
[`positionOpenings` query]: /schema/query/positionOpenings
[`PositionProfile`s]: /schema/named-type/PositionProfile
[`postingRequester`]: /schema/named-type/PostingRequester
[`postPosition` mutation]: /use-cases/job-posting/managing-job-ads/posting-a-job-ad#postposition
[`postPositionProfileForOpening` mutation]: /use-cases/job-posting/managing-job-ads/posting-a-job-ad#postpositionprofileforopening
[`updatePositionOpeningPersonContacts` mutation]: /schema/mutation/updatePositionOpeningPersonContacts
[`updatePositionOpeningStatus` mutation]: /schema/mutation/updatePositionOpeningStatus
[contact details]: /use-cases/job-posting/contact-people
[data model]: /use-cases/job-posting/key-concepts#data-model
